import { Component } from '../core/smpl-components.js'
import debounce from 'lodash/debounce'

import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { scrollContext } from '../core/scroll-context'

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)

// ------------------------------------------------------------------ //
// Fruit-machine reels.
//
// Each reel spins fast (blurred) then comes to a SUDDEN stop with a light
// overshoot bounce, exactly like a mechanical slot reel catching its detent.
// The 4 reels land on a random PERMUTATION of the 4 cards, so every reel
// shows a different item and the arrangement is fresh on every page load.
// ------------------------------------------------------------------ //

const DEFAULTS = {
	loopDuration: 0.14, // seconds for one full set of N cards to pass — the spin SPEED (lower = faster).
	spinDuration: 0.7, // seconds before the FIRST reel begins to stop.
	stagger: 0.22, // seconds between each reel coming to a stop (left → right).
}

// LANDING — the spin hands off into a single `back.out` tween that decelerates
// hard over ~DECEL_CARDS of travel, overshoots the target a touch, then settles
// back onto it. The tween's duration is derived so it BEGINS at exactly the spin
// velocity (seamless hand-off, no speed jump) and ENDS at rest on the target.
const DECEL_CARDS = 1.1 // card-heights the reel travels while stopping. Lower = harder/more sudden.
const BACK_OVERSHOOT = 1.5 // `back.out` strength → how far past the target it bounces. Higher = bouncier.

// MOTION BLUR — blur (px) on the strip while spinning, faded to 0 as it stops. 0 disables.
const SPIN_BLUR = 0

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Fisher–Yates shuffle of [0..n-1]. Reshuffles the identity order to its reverse
// so the reels always look mixed (never all-in-order).
const randomPermutation = (n) => {
	const arr = Array.from({ length: n }, (_, k) => k)
	for (let k = n - 1; k > 0; k--) {
		const j = Math.floor(Math.random() * (k + 1))
		;[arr[k], arr[j]] = [arr[j], arr[k]]
	}
	if (n > 1 && arr.every((val, idx) => val === idx)) arr.reverse()
	return arr
}

// -------------------------------------------------------------------------- //
class StatReels {
	constructor(options) {
		this.el = options.element
		this.config = this.readConfig(this.el)

		// One reel per attraction group. Each strip holds N canonical cards; we
		// duplicate them once so the loop can scroll seamlessly (2N tall).
		this.reels = Array.from(this.el.querySelectorAll(`.${options.classReel}`)).map((elReel) => {
			const elStrip = elReel.querySelector(`.${options.classStrip}`)
			const originals = Array.from(elStrip.querySelectorAll(`.${options.classItem}`))

			originals.forEach((item) => elStrip.appendChild(item.cloneNode(true)))

			return {
				elStrip,
				setY: gsap.quickSetter(elStrip, 'y', 'px'),
				n: originals.length,
				H: 0,
				target: 0, // card index this reel lands on
				settled: false,
				tweens: [],
			}
		})

		this.played = false

		this.measure()

		this.onResize = debounce(() => this.measure(), 150)
		window.addEventListener('resize', this.onResize)

		if (!this.reels.length) return

		// Hide the spinning cards until the reels start — the scallop window frame
		// stays visible on its own (its background + mask live on the window el).
		this.reels.forEach((reel) => (reel.elStrip.style.visibility = 'hidden'))

		// No animation: still present a valid, all-unique arrangement instantly.
		if (prefersReducedMotion()) {
			this.showStatic()
			return
		}

		this.scrollTrigger = ScrollTrigger.create({
			trigger: this.el,
			scroller: scrollContext.getScroller(),
			start: 'top 80%',
			once: true,
			onEnter: () => this.play(),
		})
	}

	readConfig(el) {
		const num = (attr, fallback) => {
			const raw = el.dataset[attr]
			const val = raw != null ? parseFloat(raw) : NaN
			return Number.isFinite(val) ? val : fallback
		}
		return {
			loopDuration: num('loopDuration', DEFAULTS.loopDuration),
			spinDuration: num('spinDuration', DEFAULTS.spinDuration),
			stagger: num('stagger', DEFAULTS.stagger),
		}
	}

	measure() {
		this.reels.forEach((reel) => {
			if (!reel.items) reel.items = Array.from(reel.elStrip.children)
			if (reel.items[0]) reel.H = reel.items[0].getBoundingClientRect().height
			// Keep a settled reel centred on its target after a resize (H changed).
			if (reel.settled) reel.setY(-reel.target * reel.H)
		})
	}

	// Show a random unique arrangement with no motion (reduced-motion path).
	showStatic() {
		const order = randomPermutation(this.reels.length)
		this.reels.forEach((reel, i) => {
			reel.target = order[i]
			reel.settled = true
			reel.setY(-reel.target * reel.H)
			reel.elStrip.style.visibility = ''
		})
	}

	play() {
		if (this.played || prefersReducedMotion()) return
		this.played = true

		this.measure() // fresh heights now the reels are on-screen
		const order = randomPermutation(this.reels.length)

		this.reels.forEach((reel, i) => {
			reel.elStrip.style.visibility = '' // reveal the cards as spinning begins
			const H = reel.H
			const loopY = reel.n * H // one full loop of cards
			const v = loopY / this.config.loopDuration // spin velocity (px/s)
			const target = order[i]
			reel.target = target

			const decelDist = DECEL_CARDS * H
			// Phase where the stop must BEGIN so that travelling exactly `decelDist`
			// lands on the target card. Wrapped into [0, loopY).
			const targetPhase = gsap.utils.wrap(0, loopY, target * H - decelDist)

			// Desired stop time for this reel (even left→right stagger).
			const minPos = v * (this.config.spinDuration + i * this.config.stagger)
			// Next occurrence of the aligned phase at/after that point — the spin
			// runs on until here (at most one loopDuration longer), so the landing
			// distance is ALWAYS the same short `decelDist` → a consistent, sudden stop.
			const posStart = minPos + gsap.utils.wrap(0, loopY, targetPhase - minPos)
			const spinTime = posStart / v
			const restPos = posStart + decelDist

			// `back.out(s)` begins at velocity (s+3)*dist/dur; derive dur so it starts
			// at exactly the spin velocity — a seamless hand-off into the sudden stop.
			const landDuration = ((BACK_OVERSHOOT + 3) * decelDist) / v

			const state = { pos: 0 }
			const render = () => reel.setY(-(state.pos % loopY))

			// Motion blur, full while spinning and faded out across the landing.
			const blur = { v: SPIN_BLUR }
			const applyBlur = () => (reel.elStrip.style.filter = blur.v > 0.05 ? `blur(${blur.v}px)` : '')
			if (SPIN_BLUR > 0) applyBlur()

			const tl = gsap.timeline({
				onUpdate: render,
				onComplete: () => {
					reel.settled = true
					reel.setY(-reel.target * reel.H) // snap to an exact whole-card offset
				},
			})
			tl.to(state, { pos: posStart, duration: spinTime, ease: 'none' })
			tl.to(state, { pos: restPos, duration: landDuration, ease: `back.out(${BACK_OVERSHOOT})` })
			if (SPIN_BLUR > 0) {
				tl.to(blur, { v: 0, duration: landDuration, ease: 'none', onUpdate: applyBlur, onComplete: applyBlur }, spinTime)
			}

			reel.tweens.push(tl)
		})
	}

	destroy() {
		window.removeEventListener('resize', this.onResize)
		if (this.scrollTrigger) this.scrollTrigger.kill()
		this.reels.forEach((reel) => {
			reel.tweens.forEach((t) => t.kill())
			reel.elStrip.style.filter = ''
			reel.elStrip.style.visibility = ''
		})
	}
}

// -------------------------------------------------------------------------- //
export default class extends Component {
	prepare() {
		this.statReels = new StatReels({
			element: this.element,
			classReel: 'stat-reels__reel',
			classStrip: 'stat-reels__strip',
			classItem: 'stat-reels__item',
		})

		// Clicking a reel item smooth-scrolls to its attraction group. StatReels
		// only handles the spin, so this scroll behaviour lives here. No offset —
		// the anchor targets carry it in CSS.

		this.statsLinks = this.element.querySelectorAll('.stat-reels__item a[href^="#"]')
		this.onItemClick = (e) => {
			// const link = e.target.closest('.stat-reels__item a[href^="#"]')
			// if (!link || !this.element.contains(link)) return
			const id = decodeURIComponent(e.currentTarget.hash.slice(1))
			const target = id && (document.getElementById(id) || document.getElementsByName(id)[0])
			if (!target) return
			e.preventDefault()
			e.stopPropagation()
			console.log(target)
			gsap.to(scrollContext.getScroller(), { duration: 1, ease: 'power2.inOut', scrollTo: target })
		}

		this.bndOnItemClick = this.onItemClick.bind(this)
		this.statsLinks.forEach((link) => link.addEventListener('click', this.bndOnItemClick))
	}
	mount() {}
	unmount() {
		if (this.statReels) this.statReels.destroy()
		this.statsLinks.forEach((link) => link.removeEventListener('click', this.bndOnItemClick))
	}
}
